import { FC, useEffect, useRef, useState } from "react";

interface SectionMeta {
  id: string;
  title: string;
  number?: string;
}

const SCROLL_OFFSET_PX = 120;
const SCROLL_LOCK_MAX_MS = 1000;

const getFeatureRoot = (): ParentNode =>
  document.querySelector(".selskabsblanketten-feature") ?? document;

const readSections = (root: ParentNode = getFeatureRoot()): SectionMeta[] =>
  Array.from(root.querySelectorAll<HTMLElement>("[data-form-section]"))
    .filter((element) => !!element.id)
    .map((element) => ({
      id: element.id,
      title: element.dataset.formSectionTitle ?? element.id,
      number: element.dataset.formSectionNumber,
    }));

const sectionsEqual = (previous: SectionMeta[], next: SectionMeta[]) => {
  if (previous.length !== next.length) return false;
  return previous.every(
    (section, index) =>
      section.id === next[index].id &&
      section.title === next[index].title &&
      section.number === next[index].number,
  );
};

const pickActiveSectionId = (sections: SectionMeta[]): string | null => {
  if (sections.length === 0) return null;

  let activeId = sections[0].id;

  for (const section of sections) {
    const element = document.getElementById(section.id);
    if (!element) continue;

    if (element.getBoundingClientRect().top - SCROLL_OFFSET_PX <= 0) {
      activeId = section.id;
    }
  }

  return activeId;
};

export const FormSectionsNav: FC = () => {
  const [sections, setSections] = useState<SectionMeta[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const pendingScrollTargetRef = useRef<string | null>(null);
  const scrollUnlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const scrollEndHandlerRef = useRef<(() => void) | null>(null);

  const clearScrollLock = () => {
    pendingScrollTargetRef.current = null;
    if (scrollUnlockTimerRef.current) {
      clearTimeout(scrollUnlockTimerRef.current);
      scrollUnlockTimerRef.current = null;
    }
    if (scrollEndHandlerRef.current) {
      window.removeEventListener("scrollend", scrollEndHandlerRef.current);
      scrollEndHandlerRef.current = null;
    }
  };

  const lockScrollToSection = (id: string) => {
    clearScrollLock();
    pendingScrollTargetRef.current = id;
    setActiveId(id);

    const releaseScrollLock = () => {
      clearScrollLock();
      setActiveId(pickActiveSectionId(readSections()));
    };

    scrollUnlockTimerRef.current = setTimeout(
      releaseScrollLock,
      SCROLL_LOCK_MAX_MS,
    );

    if ("onscrollend" in window) {
      const onScrollEnd = () => {
        releaseScrollLock();
      };
      scrollEndHandlerRef.current = onScrollEnd;
      window.addEventListener("scrollend", onScrollEnd, { once: true });
    }
  };

  useEffect(() => {
    let animationFrame = 0;
    const update = () => {
      const nextSections = readSections();
      setSections((current) =>
        sectionsEqual(current, nextSections) ? current : nextSections,
      );
    };

    update();

    const observer = new MutationObserver(() => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(update);
    });
    const featureRoot = getFeatureRoot();
    observer.observe(featureRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "data-form-section",
        "data-form-section-title",
        "data-form-section-number",
        "id",
      ],
    });

    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (sections.length === 0) {
      // TODO(react-hooks-7): only this empty-sections guard sets state
      // synchronously; the scroll observer below is a legitimate external
      // subscription. `activeId` could instead be derived as null when there
      // are no sections.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveId(null);
      return;
    }

    let animationFrame = 0;
    const updateActive = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const pendingTarget = pendingScrollTargetRef.current;
        if (pendingTarget) {
          setActiveId((current) =>
            current === pendingTarget ? current : pendingTarget,
          );
          return;
        }

        const nextActiveId = pickActiveSectionId(sections);
        setActiveId((current) =>
          current === nextActiveId ? current : nextActiveId,
        );
      });
    };

    updateActive();
    window.addEventListener("scroll", updateActive, { passive: true });
    window.addEventListener("resize", updateActive);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", updateActive);
      window.removeEventListener("resize", updateActive);
      clearScrollLock();
    };
  }, [sections]);

  if (sections.length < 1) {
    return null;
  }

  const handleClick =
    (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      lockScrollToSection(id);
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      history.replaceState(null, "", `#${id}`);
    };

  return (
    <nav aria-label="Indholdsfortegnelse" className="blanket-section-nav">
      <p className="small fw-semibold text-uppercase text-muted mb-3">
        Indhold
      </p>
      <ol className="list-unstyled d-flex flex-column gap-1 mb-0">
        {sections.map((section) => {
          const isActive = section.id === activeId;
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={handleClick(section.id)}
                className={`blanket-section-nav__link d-flex align-items-start gap-2 rounded px-3 py-2 small text-decoration-none ${
                  isActive ? "active fw-semibold text-dark" : "text-muted"
                }`}
              >
                {section.number ? (
                  <span
                    className={`blanket-section-nav__number rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                      isActive ? "active" : ""
                    }`}
                  >
                    {section.number}
                  </span>
                ) : null}
                <span>{section.title}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default FormSectionsNav;
